/**
 * ADR-0176 §12 — the admin "Smoke Runs" board.
 *
 * Backs `GET /internal/admin/smoke-runs*`. Same shape as
 * `TestSuiteView` — mono kicker + serif title + Card list, one row
 * per run. Poll interval: 2 seconds while the current detail row is
 * `status='running'`, static otherwise (mirrors the poll pattern in
 * apps/admin/src/views/health/HealthView.tsx and TestSuiteView.tsx).
 *
 * The board is initially list-only (recent runs); clicking a row
 * expands it to the per-skill detail. A future slice may add a
 * "Start run" affordance that lets the staff pick an account + kick
 * off a run without going through the account detail view.
 */

import { useCallback, useEffect, useState } from 'react';

import type {
  AdminSmokeRunDetail,
  AdminSmokeRunHeader,
  AdminSmokeSkillResult,
} from '@sill/types/api';

/**
 * ADR-0176 follow-up (2026-07-11) — hard cap on how much of the
 * captured `response_preview` string we ever render into the DOM.
 * The API already caps the persisted value at ~4 KB, but a defensive
 * client-side cap keeps a corrupt / stale row from ever paralysing
 * the browser. Renders a trailing ellipsis when we clip.
 */
const MAX_RENDERED_PREVIEW_CHARS = 8_192;

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import type { PillTone } from '../../components/atoms/Pill.js';
import { formatIsoCompact, formatRelativeShort } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";

const POLL_INTERVAL_MS = 2_000;

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.server_reason !== undefined && err.server_reason.length > 0) {
      return err.server_reason;
    }
    return err.kind;
  }
  return 'Unexpected error';
}

export interface SmokeRunsViewProps {
  /** Run to auto-open on mount — set when arriving here right after
   *  starting a run from the account detail view. */
  initialRunId?: string | null;
}

export function SmokeRunsView({
  initialRunId = null,
}: SmokeRunsViewProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const [runs, setRuns] = useState<readonly AdminSmokeRunHeader[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AdminSmokeRunDetail | null>(null);

  const fetchRuns = useCallback(async (): Promise<void> => {
    try {
      const res = await api.listSmokeRuns();
      setRuns(res.runs);
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'unauthorized') return;
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void fetchRuns();
  }, [fetchRuns]);

  const openRun = useCallback(
    async (runId: string): Promise<void> => {
      try {
        const detail = await api.getSmokeRun(runId);
        setSelected(detail);
      } catch (err) {
        setError(errorMessage(err));
      }
    },
    [api],
  );

  // Auto-open the run we just started (a fresh navigation carrying
  // `initialRunId` — not re-triggered on every re-render since the prop
  // is only ever set once per `setView('smoke', { smokeRunId })` call).
  useEffect(() => {
    if (initialRunId !== null) {
      void openRun(initialRunId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRunId]);

  // Drive the run forward while it's still running. The server owns the
  // sequence entirely (ADR-0176 §3) — each `advance` call processes
  // exactly one skill and returns, so the client's whole job is to keep
  // calling it until the run reaches a terminal status. `inFlight` guards
  // against a slow call (edge dispatch has up to a 10s budget) still
  // being in progress when the next 2s tick fires — firing two
  // overlapping advances on one run would just have the second harmlessly
  // 409 `already_in_flight` (ADR-0176 §3 dispatch-claim), but skipping the
  // tick is simpler and avoids a wasted round-trip.
  useEffect(() => {
    if (selected === null) return;
    if (selected.header.status !== 'running') return;
    let inFlight = false;
    const id = window.setInterval(() => {
      if (inFlight) return;
      inFlight = true;
      void (async (): Promise<void> => {
        try {
          const detail = await api.advanceSmokeRun(selected.header.smoke_run_id);
          setSelected(detail);
          void fetchRuns();
        } catch {
          // Best-effort poll; the next tick will retry.
        } finally {
          inFlight = false;
        }
      })();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [selected, api, fetchRuns]);

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-8 py-6 sm:py-10">
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        QA console
      </div>
      <h1
        style={{
          fontFamily: serifFont,
          fontSize: 'clamp(28px, 6vw, 44px)',
          lineHeight: 1,
          color: C.text,
          letterSpacing: -1.5,
          margin: 0,
        }}
      >
        Smoke Runs
      </h1>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.body,
          marginTop: 10,
          lineHeight: 1.5,
          maxWidth: 640,
        }}
      >
        Per-account agent smoke runs. Sill Heartbeat exercises the seven
        Phase-1 default skills end-to-end against each opted-in
        account&rsquo;s live sites + test-mode Stripe rail. Test-mode
        only — the harness refuses to start when the live-mode
        env-gate is present.
      </div>

      <div className="mt-6 grid gap-3">
        {loading ? (
          <Card style={{ padding: 18 }}>
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                color: C.muted,
                letterSpacing: 1.5,
              }}
            >
              Loading…
            </div>
          </Card>
        ) : error !== null ? (
          <Card style={{ padding: 18 }}>
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.coralFg,
                letterSpacing: 2,
                textTransform: 'uppercase',
                marginBottom: 6,
              }}
            >
              Load failed
            </div>
            <div style={{ fontFamily: monoFont, fontSize: 13, color: C.coralFg }}>
              {error}
            </div>
          </Card>
        ) : runs.length === 0 ? (
          <Card style={{ padding: 18 }}>
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 12,
                color: C.muted,
                letterSpacing: 0.5,
              }}
            >
              No smoke runs yet. Opt an account in from its detail view to
              begin.
            </div>
          </Card>
        ) : (
          runs.map((run) => (
            <RunHeaderRow
              key={run.smoke_run_id}
              run={run}
              onOpen={() => void openRun(run.smoke_run_id)}
              selected={selected?.header.smoke_run_id === run.smoke_run_id}
            />
          ))
        )}
      </div>

      {selected !== null ? (
        <div className="mt-6">
          <RunDetailCard detail={selected} />
        </div>
      ) : null}
    </div>
  );
}

function statusTone(status: AdminSmokeRunHeader['status']): PillTone {
  if (status === 'completed') return 'mint';
  if (status === 'aborted') return 'coral';
  if (status === 'cancelled') return 'amber';
  if (status === 'refused') return 'coral';
  if (status === 'running') return 'sky';
  return 'neutral';
}

function skillTone(
  disposition: AdminSmokeSkillResult['disposition'],
  outcome: AdminSmokeSkillResult['outcome'],
): PillTone {
  if (disposition === 'skip') return 'amber';
  if (outcome === 'passed') return 'mint';
  if (outcome === 'failed') return 'coral';
  if (outcome === 'error') return 'coral';
  return 'sky';
}

function RunHeaderRow({
  run,
  onOpen,
  selected,
}: {
  run: AdminSmokeRunHeader;
  onOpen: () => void;
  selected: boolean;
}): JSX.Element {
  const C = useC();
  return (
    <Card
      style={{
        padding: 16,
        cursor: 'pointer',
        border: selected ? `1px solid ${C.lime}` : `1px solid ${C.border}`,
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        style={{
          background: 'transparent',
          border: 0,
          padding: 0,
          width: '100%',
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Pill tone={statusTone(run.status)}>{run.status}</Pill>
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 14,
                color: C.text,
                fontWeight: 500,
                wordBreak: 'break-all',
              }}
            >
              {run.account_id}
            </div>
          </div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              letterSpacing: 0.5,
            }}
            title={formatIsoCompact(run.started_at)}
          >
            {formatRelativeShort(run.started_at)}
          </div>
        </div>
        <div
          className="flex items-center gap-3 flex-wrap"
          style={{ marginTop: 8 }}
        >
          {/* ADR-0176 addendum (2026-07-10) — surface the run_kind
              discriminator so staff can tell an ad-hoc single-skill
              run apart from a guided seven-skill run at a glance.
              Legacy rows carry `run_kind='guided'` from the migration
              default; no back-compat branch needed. */}
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: run.run_kind === 'ad_hoc' ? C.mintFg : C.muted,
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}
          >
            {run.run_kind === 'ad_hoc' ? 'ad hoc' : 'guided'}
          </span>
          {run.target_domain ? (
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                color: C.text,
                overflowWrap: 'anywhere',
              }}
              title="Site this run tested"
            >
              {run.target_domain}
            </span>
          ) : null}
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}
          >
            {run.plan?.plan_skills.length ?? 0} plan · {run.plan?.plan_skips.length ?? 0} skip
          </span>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.dim,
              letterSpacing: 1,
              textTransform: 'uppercase',
            }}
          >
            {run.smoke_run_id}
          </span>
        </div>
      </button>
    </Card>
  );
}

function RunDetailCard({ detail }: { detail: AdminSmokeRunDetail }): JSX.Element {
  const C = useC();
  return (
    <Card style={{ padding: 20 }}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          Run detail
        </div>
        <Pill tone={statusTone(detail.header.status)}>{detail.header.status}</Pill>
        {detail.header.target_domain ? (
          <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, overflowWrap: 'anywhere' }}>
            {detail.header.target_domain}
          </span>
        ) : null}
      </div>
      <div className="grid grid-cols-1 gap-2">
        {detail.skills.map((s) => (
          <SkillRow key={s.smoke_run_skill_id} skill={s} />
        ))}
      </div>
    </Card>
  );
}

function SkillRow({ skill }: { skill: AdminSmokeSkillResult }): JSX.Element {
  const C = useC();
  const [showPreview, setShowPreview] = useState(false);
  const tone = skillTone(skill.disposition, skill.outcome);
  const label =
    skill.disposition === 'skip'
      ? skill.skip_reason ?? 'skip'
      : skill.outcome ?? 'pending';
  // ADR-0176 follow-up (2026-07-11) — the raw JSON preview only
  // exists on `outcome='passed'` rows; every other branch persists
  // NULL to `response_preview`. Guarding on non-null keeps the
  // toggle from ever appearing on skip / fail / error rows where
  // there's nothing to expand.
  const hasPreview =
    typeof skill.response_preview === 'string'
    && skill.response_preview.length > 0;
  return (
    <div
      style={{
        background: C.surface2,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
      }}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap px-3 py-2">
        <div className="flex items-center gap-3 min-w-0">
          <Pill tone={tone}>{label}</Pill>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.text,
              letterSpacing: 0.5,
            }}
          >
            #{skill.ordinal} · {skill.skill}
          </span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {skill.record_id !== null ? (
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.dim,
                letterSpacing: 1,
              }}
              title={skill.record_id}
            >
              rec {skill.record_id.slice(0, 12)}…
            </span>
          ) : null}
          {skill.stripe_pi !== null ? (
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.dim,
                letterSpacing: 1,
              }}
            >
              {skill.stripe_pi}
            </span>
          ) : null}
          {skill.latency_ms !== null ? (
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                letterSpacing: 1,
              }}
            >
              {skill.latency_ms}ms
            </span>
          ) : null}
          {hasPreview ? (
            <button
              type="button"
              onClick={() => setShowPreview((v) => !v)}
              style={{
                background: 'transparent',
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                cursor: 'pointer',
                padding: '2px 8px',
                fontFamily: monoFont,
                fontSize: 10,
                color: C.body,
                letterSpacing: 1,
                textTransform: 'uppercase',
              }}
              aria-expanded={showPreview}
            >
              {showPreview ? 'Hide response' : 'Response'}
            </button>
          ) : null}
        </div>
      </div>
      {hasPreview && showPreview ? (
        <ResponsePreviewBlock raw={skill.response_preview as string} />
      ) : null}
    </div>
  );
}

/**
 * Render the persisted `response_preview` JSON string in a
 * monospace block. Attempts to pretty-print via `JSON.parse` +
 * `JSON.stringify(_, null, 2)`; falls back to the raw string
 * verbatim if parse fails (which will happen deliberately on any
 * overflow-truncated preview — see the API's
 * `MAX_RESPONSE_PREVIEW_BYTES` sentinel discipline). A hard
 * client-side character cap
 * ({@link MAX_RENDERED_PREVIEW_CHARS}) is applied on top so a
 * malformed row can never freeze the browser layout.
 */
function ResponsePreviewBlock({ raw }: { raw: string }): JSX.Element {
  const C = useC();
  const clipped =
    raw.length > MAX_RENDERED_PREVIEW_CHARS
      ? raw.slice(0, MAX_RENDERED_PREVIEW_CHARS) + '…'
      : raw;
  let display: string;
  try {
    display = JSON.stringify(JSON.parse(clipped), null, 2);
  } catch {
    // Parse failed — most commonly because the stored preview was
    // truncated at the API layer with a `…truncated` sentinel that
    // makes the string non-JSON-valid, or because a future edge
    // handler emits a shape we can't parse. Fall back to the raw
    // string verbatim rather than crashing the row.
    display = clipped;
  }
  return (
    <div
      style={{
        borderTop: `1px solid ${C.borderSoft}`,
        padding: '10px 12px',
        background: C.surface,
      }}
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 6,
        }}
      >
        Response preview · staff diagnostic
      </div>
      <pre
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          lineHeight: 1.4,
          color: C.text,
          background: C.surface2,
          border: `1px solid ${C.borderSoft}`,
          borderRadius: 2,
          padding: 10,
          margin: 0,
          overflow: 'auto',
          maxHeight: 320,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {display}
      </pre>
    </div>
  );
}
